import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast, Toaster } from 'react-hot-toast';
import {
  FaArrowLeft,
  FaBriefcase,
  FaCopy,
  FaDownload,
  FaEdit,
  FaLink,
  FaPlus,
  FaSync,
  FaTrash,
  FaTimes,
  FaWhatsapp,
} from 'react-icons/fa';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';

const EMPTY_FORM = {
  company_name: '',
  job_post: 'Machine operator',
  description: '',
  location: '',
  gender_preference: 'Male/Female',
  qualification: '',
  trade: 'All',
  age_range: '18 to 28',
  contact_person_name: '',
  contact_person_number: '',
  priority: '',
  total_vacancy: '',
  canteen: '',
  transport_facility: '',
  salary: '',
  hours: '8 Hours',
};

const TABLE_COLUMNS = [
  { key: 'company_name', label: 'Company' },
  { key: 'job_post', label: 'Job post' },
  { key: 'location', label: 'Location' },
  { key: 'gender_preference', label: 'Gender' },
  { key: 'qualification', label: 'Qualification' },
  { key: 'trade', label: 'Trade' },
  { key: 'age_range', label: 'Age' },
  { key: 'contact_person_name', label: 'Contact person' },
  { key: 'contact_person_number', label: 'Contact number' },
  { key: 'canteen', label: 'Canteen' },
  { key: 'transport_facility', label: 'Transport' },
  { key: 'salary', label: 'Salary' },
  { key: 'hours', label: 'Hours' },
];

/** Excel-style export columns (matches original sheet + share link) */
const EXPORT_COLUMNS = [
  { key: 'company_name', label: 'Company name' },
  { key: 'job_post', label: 'Job post' },
  { key: 'description', label: 'Description' },
  { key: 'location', label: 'Location' },
  { key: 'gender_preference', label: 'Gender preference' },
  { key: 'qualification', label: 'Qualification' },
  { key: 'trade', label: 'Trade' },
  { key: 'age_range', label: 'Age' },
  { key: 'contact_person_name', label: 'Contact person name' },
  { key: 'contact_person_number', label: 'Contact person number' },
  { key: 'priority', label: 'Priority' },
  { key: 'total_vacancy', label: 'Total Vacancy' },
  { key: 'canteen', label: 'Canteen' },
  { key: 'transport_facility', label: 'Transport Facility' },
  { key: 'salary', label: 'Salary' },
  { key: 'hours', label: 'Hours' },
  { key: 'share_link', label: 'Share link' },
];

function csvEscape(value) {
  const s = value == null ? '' : String(value);
  if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

function authHeaders() {
  return {
    Authorization: `Bearer ${localStorage.getItem('gogig_supervisor_access')}`,
    'Content-Type': 'application/json',
  };
}

function copyText(text, message) {
  if (!text) return;
  navigator.clipboard.writeText(text).then(() => toast.success(message)).catch(() => {
    const el = document.createElement('textarea');
    el.value = text;
    document.body.appendChild(el);
    el.select();
    document.execCommand('copy');
    document.body.removeChild(el);
    toast.success(message);
  });
}

function SupervisorJobInterestPage() {
  const navigate = useNavigate();
  const [tab, setTab] = useState('jobs');
  const [supervisorId, setSupervisorId] = useState(null);
  const [allJobsLink, setAllJobsLink] = useState('');
  const [openings, setOpenings] = useState([]);
  const [candidates, setCandidates] = useState([]);
  const [candidateCount, setCandidateCount] = useState(0);
  const [loadingJobs, setLoadingJobs] = useState(true);
  const [loadingApps, setLoadingApps] = useState(false);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');
  const [appsSearch, setAppsSearch] = useState('');
  const [expandedCandidate, setExpandedCandidate] = useState(null);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);

  const loadMeta = useCallback(async () => {
    const res = await fetch(`${API_URL}/api/job-interest/manage/link`, { headers: authHeaders() });
    const data = await res.json();
    if (!data.success) throw new Error(data.message || 'Failed to load link');
    setSupervisorId(data.supervisorId);
    setAllJobsLink(`${window.location.origin}${data.path}`);
  }, []);

  const loadOpenings = useCallback(async () => {
    setLoadingJobs(true);
    try {
      const res = await fetch(`${API_URL}/api/job-interest/manage/openings`, { headers: authHeaders() });
      const data = await res.json();
      if (!data.success) throw new Error(data.message || 'Failed to load jobs');
      setOpenings(data.openings || []);
    } catch (err) {
      console.error(err);
      toast.error(err.message || 'Failed to load jobs');
    } finally {
      setLoadingJobs(false);
    }
  }, []);

  const loadSubmissions = useCallback(async () => {
    setLoadingApps(true);
    try {
      const res = await fetch(`${API_URL}/api/job-interest/manage/submissions`, { headers: authHeaders() });
      const data = await res.json();
      if (!data.success) throw new Error(data.message || 'Failed to load applications');
      setCandidates(data.candidates || []);
      setCandidateCount(data.candidate_count ?? (data.candidates || []).length);
    } catch (err) {
      console.error(err);
      toast.error(err.message || 'Failed to load applications');
    } finally {
      setLoadingApps(false);
    }
  }, []);

  useEffect(() => {
    (async () => {
      try {
        await loadMeta();
        await loadOpenings();
      } catch (err) {
        toast.error(err.message || 'Failed to open job interest');
      }
    })();
  }, [loadMeta, loadOpenings]);

  const filteredOpenings = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return openings;
    return openings.filter((job) =>
      [
        job.company_name,
        job.job_post,
        job.location,
        job.qualification,
        job.contact_person_name,
        job.contact_person_number,
        job.salary,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
        .includes(q)
    );
  }, [openings, search]);

  const jobLink = (jobId) =>
    `${window.location.origin}/job-interest/${supervisorId}/job/${jobId}`;

  const filteredCandidates = useMemo(() => {
    const q = appsSearch.trim().toLowerCase();
    if (!q) return candidates;
    return candidates.filter((c) =>
      [
        c.candidate_name,
        c.mobile,
        c.jobs_summary,
        ...(c.companies || []),
        ...(c.work_locations || []),
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
        .includes(q)
    );
  }, [candidates, appsSearch]);

  const shareWhatsApp = () => {
    if (!allJobsLink) return;
    const text = encodeURIComponent(
      `Hi! Browse open job roles and apply here:\n${allJobsLink}`
    );
    window.open(`https://wa.me/?text=${text}`, '_blank', 'noopener,noreferrer');
  };

  const openAdd = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setEditorOpen(true);
  };

  const openEdit = (job) => {
    setEditingId(job.id);
    setForm({
      company_name: job.company_name || '',
      job_post: job.job_post || '',
      description: job.description || '',
      location: job.location || '',
      gender_preference: job.gender_preference || '',
      qualification: job.qualification || '',
      trade: job.trade || '',
      age_range: job.age_range || '',
      contact_person_name: job.contact_person_name || '',
      contact_person_number: job.contact_person_number || '',
      priority: job.priority || '',
      total_vacancy: job.total_vacancy || '',
      canteen: job.canteen || '',
      transport_facility: job.transport_facility || '',
      salary: job.salary || '',
      hours: job.hours || '',
    });
    setEditorOpen(true);
  };

  const onFormChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const saveJob = async (e) => {
    e.preventDefault();
    if (!form.company_name.trim() || !form.job_post.trim() || !form.location.trim()) {
      toast.error('Company, job post and location are required');
      return;
    }
    setSaving(true);
    try {
      const url = editingId
        ? `${API_URL}/api/job-interest/manage/openings/${editingId}`
        : `${API_URL}/api/job-interest/manage/openings`;
      const res = await fetch(url, {
        method: editingId ? 'PUT' : 'POST',
        headers: authHeaders(),
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.message || 'Save failed');
      toast.success(editingId ? 'Job updated' : 'Job added');
      setEditorOpen(false);
      setEditingId(null);
      setForm(EMPTY_FORM);
      loadOpenings();
    } catch (err) {
      console.error(err);
      toast.error(err.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const removeJob = async (id) => {
    if (!window.confirm('Remove this job from the interest list?')) return;
    try {
      const res = await fetch(`${API_URL}/api/job-interest/manage/openings/${id}`, {
        method: 'DELETE',
        headers: authHeaders(),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.message || 'Remove failed');
      toast.success('Job removed');
      setOpenings((prev) => prev.filter((j) => j.id !== id));
    } catch (err) {
      console.error(err);
      toast.error(err.message || 'Remove failed');
    }
  };

  const exportJobsSheet = () => {
    if (!supervisorId) {
      toast.error('Supervisor link not ready yet');
      return;
    }
    const rows = filteredOpenings.length ? filteredOpenings : openings;
    if (!rows.length) {
      toast.error('No jobs to export');
      return;
    }

    const header = EXPORT_COLUMNS.map((c) => csvEscape(c.label)).join(',');
    const body = rows
      .map((job) =>
        EXPORT_COLUMNS.map((col) => {
          if (col.key === 'share_link') {
            return csvEscape(jobLink(job.id));
          }
          return csvEscape(job[col.key] ?? '');
        }).join(',')
      )
      .join('\n');

    const csv = `\uFEFF${header}\n${body}`;
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    const stamp = new Date().toISOString().slice(0, 10);
    a.href = url;
    a.download = `job-interest-openings-${stamp}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success(`Exported ${rows.length} job(s) with share links`);
  };

  return (
    <div style={styles.page}>
      <Toaster position="top-center" />
      <div style={styles.header}>
        <div style={styles.headerLeft}>
          <button type="button" onClick={() => navigate('/supervisor/dashboard')} style={styles.backBtn}>
            <FaArrowLeft /> Dashboard
          </button>
          <div>
            <h1 style={styles.title}>
              <FaBriefcase style={{ marginRight: 10, verticalAlign: 'middle' }} />
              Job Interest
            </h1>
            <p style={styles.subtitle}>Excel-style openings list · share links · applications</p>
          </div>
        </div>
        <div style={styles.headerActions}>
          <button type="button" onClick={() => { loadOpenings(); if (tab === 'applies') loadSubmissions(); }} style={styles.ghostBtn}>
            <FaSync /> Refresh
          </button>
          <button
            type="button"
            onClick={exportJobsSheet}
            style={styles.ghostBtn}
            disabled={!openings.length || !supervisorId}
            title="Export jobs sheet with share links"
          >
            <FaDownload /> Export sheet
          </button>
          <button type="button" onClick={openAdd} style={styles.primaryBtn}>
            <FaPlus /> Add job
          </button>
        </div>
      </div>

      <div style={styles.shareCard}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={styles.shareLabel}>WhatsApp share link (all jobs)</div>
          <div style={styles.shareUrl}>{allJobsLink || 'Loading…'}</div>
          <p style={styles.shareHint}>
            One generic link — candidates see the full job list, can open any role, and apply to multiple companies.
          </p>
        </div>
        <div style={styles.shareActions}>
          <button
            type="button"
            onClick={() => copyText(allJobsLink, 'Share link copied')}
            style={styles.copyBtn}
            disabled={!allJobsLink}
          >
            <FaCopy /> Copy
          </button>
          <button
            type="button"
            onClick={shareWhatsApp}
            style={styles.waBtn}
            disabled={!allJobsLink}
          >
            <FaWhatsapp /> WhatsApp
          </button>
        </div>
      </div>

      <div style={styles.tabs}>
        <button
          type="button"
          onClick={() => setTab('jobs')}
          style={tab === 'jobs' ? styles.tabActive : styles.tab}
        >
          Jobs table ({openings.length})
        </button>
        <button
          type="button"
          onClick={() => {
            setTab('applies');
            loadSubmissions();
          }}
          style={tab === 'applies' ? styles.tabActive : styles.tab}
        >
          Candidates ({candidateCount})
        </button>
      </div>

      {tab === 'jobs' ? (
        <>
          <div style={styles.toolbar}>
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search company, location, contact…"
              style={styles.search}
            />
            <span style={styles.count}>{filteredOpenings.length} shown</span>
            <button
              type="button"
              onClick={exportJobsSheet}
              style={styles.exportBtn}
              disabled={!filteredOpenings.length || !supervisorId}
            >
              <FaDownload /> Export CSV
            </button>
          </div>

          <div style={styles.tableWrap}>
            {loadingJobs ? (
              <div style={styles.empty}>Loading jobs…</div>
            ) : filteredOpenings.length === 0 ? (
              <div style={styles.empty}>No jobs found. Add one or wait for seed data after backend restart.</div>
            ) : (
              <table style={styles.table}>
                <thead>
                  <tr>
                    {TABLE_COLUMNS.map((col) => (
                      <th key={col.key} style={styles.th}>{col.label}</th>
                    ))}
                    <th style={{ ...styles.th, position: 'sticky', right: 0, background: '#f0fdfa', zIndex: 2 }}>
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filteredOpenings.map((job, idx) => (
                    <tr key={job.id} style={{ background: idx % 2 ? '#f8fafc' : '#fff' }}>
                      {TABLE_COLUMNS.map((col) => (
                        <td key={col.key} style={styles.td} title={job[col.key] || ''}>
                          {job[col.key] || '—'}
                        </td>
                      ))}
                      <td style={{ ...styles.td, ...styles.actionsCell }}>
                        <button type="button" title="Copy job link" onClick={() => copyText(jobLink(job.id), 'Job link copied')} style={styles.iconBtnTeal}>
                          <FaLink />
                        </button>
                        <button type="button" title="Edit" onClick={() => openEdit(job)} style={styles.iconBtnBlue}>
                          <FaEdit />
                        </button>
                        <button type="button" title="Remove" onClick={() => removeJob(job.id)} style={styles.iconBtnRed}>
                          <FaTrash />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </>
      ) : (
        <div style={styles.appsWrap}>
          <div style={styles.toolbar}>
            <input
              value={appsSearch}
              onChange={(e) => setAppsSearch(e.target.value)}
              placeholder="Search candidate, mobile, company…"
              style={styles.search}
            />
            <span style={styles.count}>
              {filteredCandidates.length} unique candidate{filteredCandidates.length === 1 ? '' : 's'}
            </span>
          </div>
          {loadingApps ? (
            <div style={styles.empty}>Loading candidates…</div>
          ) : filteredCandidates.length === 0 ? (
            <div style={styles.empty}>No applications yet. Share the WhatsApp link above.</div>
          ) : (
            <div style={styles.candidateList}>
              {filteredCandidates.map((c) => {
                const open = expandedCandidate === c.key;
                return (
                  <div key={c.key} style={styles.candidateCard}>
                    <button
                      type="button"
                      style={styles.candidateHead}
                      onClick={() => setExpandedCandidate(open ? null : c.key)}
                    >
                      <div style={{ minWidth: 0, flex: 1, textAlign: 'left' }}>
                        <div style={styles.candidateName}>{c.candidate_name || '—'}</div>
                        <div style={styles.candidateMeta}>
                          {c.mobile ? (
                            <a
                              href={`tel:${c.mobile}`}
                              onClick={(e) => e.stopPropagation()}
                              style={styles.mobileLink}
                            >
                              {c.mobile}
                            </a>
                          ) : (
                            'No mobile'
                          )}
                          {' · '}
                          {c.selected_jobs?.length || 0} compan
                          {(c.selected_jobs?.length || 0) === 1 ? 'y' : 'ies'}
                          {c.last_applied_at
                            ? ` · last ${new Date(c.last_applied_at).toLocaleDateString('en-IN')}`
                            : ''}
                        </div>
                        <div style={styles.chipRow}>
                          {(c.selected_jobs || []).slice(0, open ? 99 : 4).map((job) => (
                            <span key={job.id} style={styles.companyChip} title={job.location || ''}>
                              {job.company_name}
                            </span>
                          ))}
                          {!open && (c.selected_jobs || []).length > 4 ? (
                            <span style={styles.moreChip}>+{(c.selected_jobs || []).length - 4}</span>
                          ) : null}
                        </div>
                      </div>
                      <span style={styles.expandHint}>{open ? 'Hide' : 'Details'}</span>
                    </button>
                    {open ? (
                      <div style={styles.candidateBody}>
                        <div style={styles.detailGrid}>
                          <div><span style={styles.detailLabel}>Age</span>{c.age || '—'}</div>
                          <div><span style={styles.detailLabel}>Gender</span>{c.gender || '—'}</div>
                          <div><span style={styles.detailLabel}>Qualification</span>{c.qualification || '—'}</div>
                          <div><span style={styles.detailLabel}>Trade</span>{c.trade || '—'}</div>
                          <div style={{ gridColumn: '1 / -1' }}>
                            <span style={styles.detailLabel}>Home</span>{c.home_location || '—'}
                          </div>
                          <div style={{ gridColumn: '1 / -1' }}>
                            <span style={styles.detailLabel}>Work locations</span>
                            {(c.work_locations || []).join(' · ') || '—'}
                          </div>
                        </div>
                        <div style={styles.jobsAppliedTitle}>Companies / roles applied</div>
                        <div style={{ display: 'grid', gap: 8 }}>
                          {(c.selected_jobs || []).map((job) => (
                            <div key={job.id} style={styles.jobAppliedRow}>
                              <div>
                                <strong>{job.company_name}</strong>
                                <div style={{ fontSize: 13, color: '#475569' }}>{job.job_post}</div>
                                <div style={{ fontSize: 12, color: '#94a3b8' }}>{job.location}</div>
                              </div>
                              {job.contact_person_number ? (
                                <a href={`tel:${job.contact_person_number}`} style={styles.contactMini}>
                                  {job.contact_person_name || 'Call'}
                                </a>
                              ) : null}
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : null}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {editorOpen ? (
        <div style={styles.modalBg} onClick={() => setEditorOpen(false)}>
          <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHead}>
              <h2 style={{ margin: 0, fontSize: 20, color: '#0f766e' }}>
                {editingId ? 'Edit job' : 'Add job'}
              </h2>
              <button type="button" onClick={() => setEditorOpen(false)} style={styles.closeBtn}>
                <FaTimes />
              </button>
            </div>
            <form onSubmit={saveJob} style={{ display: 'grid', gap: 10 }}>
              <div style={styles.formGrid}>
                {[
                  ['company_name', 'Company name *'],
                  ['job_post', 'Job post *'],
                  ['location', 'Location *'],
                  ['gender_preference', 'Gender preference'],
                  ['qualification', 'Qualification'],
                  ['trade', 'Trade'],
                  ['age_range', 'Age'],
                  ['contact_person_name', 'Contact person name'],
                  ['contact_person_number', 'Contact person number'],
                  ['canteen', 'Canteen'],
                  ['transport_facility', 'Transport facility'],
                  ['salary', 'Salary'],
                  ['hours', 'Hours'],
                  ['total_vacancy', 'Total vacancy (internal)'],
                  ['priority', 'Priority (internal)'],
                ].map(([name, placeholder]) => (
                  <input
                    key={name}
                    name={name}
                    value={form[name]}
                    onChange={onFormChange}
                    placeholder={placeholder}
                    style={{
                      ...styles.input,
                      gridColumn: name === 'location' ? '1 / -1' : undefined,
                    }}
                  />
                ))}
                <textarea
                  name="description"
                  value={form.description}
                  onChange={onFormChange}
                  placeholder="Description"
                  rows={3}
                  style={{ ...styles.input, gridColumn: '1 / -1', resize: 'vertical' }}
                />
              </div>
              <button type="submit" disabled={saving} style={styles.primaryBtn}>
                {saving ? 'Saving…' : editingId ? 'Save changes' : 'Add job'}
              </button>
            </form>
          </div>
        </div>
      ) : null}
    </div>
  );
}

const styles = {
  page: {
    minHeight: '100vh',
    background: 'linear-gradient(180deg, #ecfdf5 0%, #f8fafc 180px, #f1f5f9 100%)',
    padding: '20px 16px 40px',
    boxSizing: 'border-box',
    fontFamily: '"Segoe UI", system-ui, sans-serif',
  },
  header: {
    maxWidth: 1280,
    margin: '0 auto 16px',
    display: 'flex',
    flexWrap: 'wrap',
    gap: 12,
    justifyContent: 'space-between',
    alignItems: 'flex-start',
  },
  headerLeft: { display: 'flex', gap: 14, alignItems: 'flex-start' },
  backBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    border: '1px solid #cbd5e1',
    background: '#fff',
    borderRadius: 10,
    padding: '10px 12px',
    cursor: 'pointer',
    fontWeight: 600,
    color: '#334155',
  },
  title: { margin: 0, fontSize: 24, color: '#0f172a', fontWeight: 800 },
  subtitle: { margin: '4px 0 0', color: '#64748b', fontSize: 14 },
  headerActions: { display: 'flex', gap: 8, flexWrap: 'wrap' },
  primaryBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    border: 'none',
    background: 'linear-gradient(135deg, #0d9488, #0f766e)',
    color: '#fff',
    borderRadius: 12,
    padding: '11px 16px',
    fontWeight: 700,
    cursor: 'pointer',
  },
  ghostBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    border: '1px solid #cbd5e1',
    background: '#fff',
    color: '#334155',
    borderRadius: 12,
    padding: '11px 14px',
    fontWeight: 600,
    cursor: 'pointer',
  },
  shareCard: {
    maxWidth: 1280,
    margin: '0 auto 16px',
    background: '#fff',
    borderRadius: 16,
    padding: 16,
    display: 'flex',
    gap: 12,
    flexWrap: 'wrap',
    alignItems: 'center',
    boxShadow: '0 8px 24px rgba(15,23,42,0.06)',
  },
  shareLabel: { fontSize: 12, fontWeight: 700, color: '#0f766e', textTransform: 'uppercase', letterSpacing: '0.04em' },
  shareUrl: { marginTop: 4, fontSize: 13, color: '#334155', wordBreak: 'break-all' },
  shareHint: { margin: '8px 0 0', fontSize: 12, color: '#64748b', lineHeight: 1.45 },
  shareActions: { display: 'flex', gap: 8, flexWrap: 'wrap' },
  copyBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    background: '#ccfbf1',
    color: '#0f766e',
    border: 'none',
    borderRadius: 10,
    padding: '10px 14px',
    fontWeight: 700,
    cursor: 'pointer',
    whiteSpace: 'nowrap',
  },
  waBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    background: '#25D366',
    color: '#fff',
    border: 'none',
    borderRadius: 10,
    padding: '10px 14px',
    fontWeight: 700,
    cursor: 'pointer',
    whiteSpace: 'nowrap',
  },
  tabs: {
    maxWidth: 1280,
    margin: '0 auto 12px',
    display: 'flex',
    gap: 8,
  },
  tab: {
    border: 'none',
    background: '#e2e8f0',
    color: '#334155',
    borderRadius: 999,
    padding: '9px 16px',
    fontWeight: 700,
    cursor: 'pointer',
    fontSize: 13,
  },
  tabActive: {
    border: 'none',
    background: '#0d9488',
    color: '#fff',
    borderRadius: 999,
    padding: '9px 16px',
    fontWeight: 700,
    cursor: 'pointer',
    fontSize: 13,
  },
  toolbar: {
    maxWidth: 1280,
    margin: '0 auto 10px',
    display: 'flex',
    gap: 12,
    alignItems: 'center',
  },
  search: {
    flex: 1,
    padding: '11px 14px',
    borderRadius: 12,
    border: '1px solid #cbd5e1',
    fontSize: 14,
    background: '#fff',
  },
  count: { fontSize: 13, color: '#64748b', fontWeight: 600, whiteSpace: 'nowrap' },
  exportBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    border: '1px solid #0d9488',
    background: '#ecfdf5',
    color: '#0f766e',
    borderRadius: 12,
    padding: '10px 14px',
    fontWeight: 700,
    cursor: 'pointer',
    whiteSpace: 'nowrap',
  },
  tableWrap: {
    maxWidth: 1280,
    margin: '0 auto',
    background: '#fff',
    borderRadius: 16,
    overflow: 'auto',
    boxShadow: '0 8px 24px rgba(15,23,42,0.06)',
    maxHeight: 'calc(100vh - 280px)',
  },
  appsWrap: { maxWidth: 1280, margin: '0 auto' },
  candidateList: {
    display: 'grid',
    gap: 12,
  },
  candidateCard: {
    background: '#fff',
    borderRadius: 16,
    boxShadow: '0 8px 24px rgba(15,23,42,0.06)',
    overflow: 'hidden',
  },
  candidateHead: {
    width: '100%',
    display: 'flex',
    gap: 12,
    alignItems: 'flex-start',
    padding: 16,
    border: 'none',
    background: 'transparent',
    cursor: 'pointer',
  },
  candidateName: {
    fontSize: 17,
    fontWeight: 800,
    color: '#0f172a',
  },
  candidateMeta: {
    marginTop: 4,
    fontSize: 13,
    color: '#64748b',
  },
  mobileLink: {
    color: '#0f766e',
    fontWeight: 700,
    textDecoration: 'none',
  },
  chipRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 6,
    marginTop: 10,
  },
  companyChip: {
    display: 'inline-block',
    padding: '5px 10px',
    borderRadius: 999,
    background: '#ecfdf5',
    color: '#0f766e',
    fontSize: 12,
    fontWeight: 700,
    maxWidth: 220,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  moreChip: {
    display: 'inline-block',
    padding: '5px 10px',
    borderRadius: 999,
    background: '#e2e8f0',
    color: '#475569',
    fontSize: 12,
    fontWeight: 700,
  },
  expandHint: {
    fontSize: 12,
    fontWeight: 700,
    color: '#0d9488',
    whiteSpace: 'nowrap',
    marginTop: 4,
  },
  candidateBody: {
    padding: '0 16px 16px',
    borderTop: '1px solid #f1f5f9',
  },
  detailGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
    gap: 10,
    paddingTop: 14,
    fontSize: 14,
    color: '#334155',
  },
  detailLabel: {
    display: 'block',
    fontSize: 11,
    fontWeight: 700,
    color: '#94a3b8',
    textTransform: 'uppercase',
    letterSpacing: '0.03em',
    marginBottom: 2,
  },
  jobsAppliedTitle: {
    margin: '14px 0 8px',
    fontSize: 12,
    fontWeight: 800,
    color: '#0f766e',
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
  },
  jobAppliedRow: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: 12,
    alignItems: 'center',
    padding: 12,
    borderRadius: 12,
    background: '#f8fafc',
    border: '1px solid #e2e8f0',
  },
  contactMini: {
    fontSize: 12,
    fontWeight: 700,
    color: '#0f766e',
    textDecoration: 'none',
    whiteSpace: 'nowrap',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: 13,
    minWidth: 1100,
  },
  th: {
    textAlign: 'left',
    padding: '12px 10px',
    background: '#f0fdfa',
    color: '#134e4a',
    fontWeight: 800,
    fontSize: 11,
    textTransform: 'uppercase',
    letterSpacing: '0.03em',
    borderBottom: '1px solid #ccfbf1',
    whiteSpace: 'nowrap',
    position: 'sticky',
    top: 0,
    zIndex: 1,
  },
  td: {
    padding: '10px',
    borderBottom: '1px solid #f1f5f9',
    color: '#334155',
    verticalAlign: 'top',
    maxWidth: 180,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  actionsCell: {
    position: 'sticky',
    right: 0,
    background: 'inherit',
    whiteSpace: 'nowrap',
    maxWidth: 'none',
    display: 'flex',
    gap: 6,
    boxShadow: '-8px 0 12px rgba(15,23,42,0.04)',
  },
  iconBtnTeal: {
    border: 'none',
    background: '#ccfbf1',
    color: '#0f766e',
    borderRadius: 8,
    padding: '8px 9px',
    cursor: 'pointer',
  },
  iconBtnBlue: {
    border: 'none',
    background: '#dbeafe',
    color: '#1d4ed8',
    borderRadius: 8,
    padding: '8px 9px',
    cursor: 'pointer',
  },
  iconBtnRed: {
    border: 'none',
    background: '#fee2e2',
    color: '#b91c1c',
    borderRadius: 8,
    padding: '8px 9px',
    cursor: 'pointer',
  },
  empty: {
    padding: 40,
    textAlign: 'center',
    color: '#64748b',
  },
  modalBg: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(15,23,42,0.45)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    padding: 16,
  },
  modal: {
    background: '#fff',
    borderRadius: 16,
    padding: 20,
    width: '100%',
    maxWidth: 720,
    maxHeight: '90vh',
    overflow: 'auto',
  },
  modalHead: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 14,
  },
  closeBtn: {
    border: 'none',
    background: 'transparent',
    fontSize: 18,
    cursor: 'pointer',
    color: '#64748b',
  },
  formGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
    gap: 10,
  },
  input: {
    width: '100%',
    boxSizing: 'border-box',
    padding: '11px 12px',
    borderRadius: 10,
    border: '1px solid #cbd5e1',
    fontSize: 14,
  },
};

export default SupervisorJobInterestPage;
